import type { ImpersonationTargetWorkspace } from "@unbox/api";
import { Star01 } from "@untitledui/icons";
import { type KeyboardEvent, type ReactNode, useEffect, useRef } from "react";
import { Avatar } from "@/components/base/avatar/avatar";
import { cx } from "@/utils/cx";
import { fullName, initials, type ListUser, merchantCount, roleSummaryColor, workspaceRoleSummary } from "./model";
import { PillList } from "./pill-list";

export const Highlight = ({ text, query }: { text: string; query: string }) => {
  const needle = query.trim();
  if (!needle) return <>{text}</>;
  const start = text.toLowerCase().indexOf(needle.toLowerCase());
  if (start < 0) return <>{text}</>;
  const end = start + needle.length;
  return (
    <>
      {text.slice(0, start)}
      <mark className="rounded-xs bg-warning-200 text-inherit">{text.slice(start, end)}</mark>
      {text.slice(end)}
    </>
  );
};

export const FavouriteButton = ({ on, onToggle }: { on: boolean; onToggle: () => void }) => (
  <button
    type="button"
    aria-label={on ? "Remove from favourites" : "Add to favourites"}
    aria-pressed={on}
    onClick={(event) => {
      event.stopPropagation();
      onToggle();
    }}
    className={cx(
      "inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors",
      on ? "text-warning-600" : "text-gray-300 hover:bg-gray-100 hover:text-gray-500",
    )}
  >
    <Star01 className={cx("size-4", on && "fill-current")} aria-hidden="true" />
  </button>
);

type RowShellProps = { selected: boolean; onSelect: () => void; children: ReactNode };

/**
 * Rows are divs rather than buttons because the favourite star inside them is a
 * button of its own. The modal's Enter shortcut skips events raised inside a row so
 * selecting with the keyboard never also starts a session.
 */
const RowShell = ({ selected, onSelect, children }: RowShellProps) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView({ block: "nearest" });
  }, [selected]);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect();
    }
  };
  return (
    <div
      ref={ref}
      data-row
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      className={cx(
        "flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left outline-hidden transition-colors focus-visible:bg-gray-50",
        selected ? "bg-purple-50" : "hover:bg-gray-50",
      )}
    >
      {children}
    </div>
  );
};

type RowProps = {
  user: ListUser;
  workspaces: ImpersonationTargetWorkspace[];
  selected: boolean;
  onSelect: () => void;
  favourite: boolean;
  onToggleFavourite: () => void;
};

export const UserRow = ({
  user,
  workspaces,
  query,
  selected,
  compact,
  onSelect,
  favourite,
  onToggleFavourite,
}: RowProps & {
  query: string;
  /** The row shares the modal with the details pane, so fewer pills fit beside the name. */
  compact: boolean;
}) => {
  const name = fullName(user);
  const count = merchantCount(workspaces);
  return (
    <RowShell selected={selected} onSelect={onSelect}>
      <Avatar size="md" initials={initials(name)} alt={name} />
      <span className="flex min-w-[140px] flex-1 flex-col">
        <span className="truncate text-md font-semibold text-gray-900">
          <Highlight text={name} query={query} />
        </span>
        <span className="truncate text-sm text-gray-500">
          <Highlight text={user.email} query={query} />
        </span>
      </span>
      <PillList
        nowrap
        className="shrink justify-end"
        max={compact ? 1 : 2}
        noun={{ one: "workspace", many: "workspaces" }}
        pills={workspaces.map((workspace) => {
          const summary = workspaceRoleSummary(workspace);
          return { key: workspace.id, label: `${workspace.name} · ${summary}`, color: roleSummaryColor(summary) };
        })}
      />
      <span className="shrink-0 whitespace-nowrap text-right text-sm text-gray-500 tabular-nums">
        {count} merchant{count === 1 ? "" : "s"}
      </span>
      <FavouriteButton on={favourite} onToggle={onToggleFavourite} />
    </RowShell>
  );
};
